import { Service, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxIconName, OnyxNumberRange } from '@onyx/angular';
import { OnyxLanguagePipe, OnyxModalService } from '@onyx/angular';
import {
  chain,
  intersection,
  isArray,
  isBoolean,
  isEqual,
  isNumber,
  isString,
  pick,
} from 'lodash';
import { BASE_PARAMETERS_KEYS } from '../../../../common/constants/common/base-parameters-keys';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { GoodsSizeType } from '../../../../common/enums/goods-size-type';
import type { Parameter } from '../../../../common/helpers/common.helper';
import { CommonHelper } from '../../../../common/helpers/common.helper';
import type { BaseParameters } from '../../../../common/interfaces/common/base-parameters';
import { JoinPipe } from '../../../../common/pipes/join.pipe';
import { DriverHelper } from '../../../drivers/common/helpers/driver.helper';
import type {
  Driver,
  SimplifiedDriver,
} from '../../../drivers/common/interfaces/driver';
import { VEHICLE_CATEGORIES } from '../../../fleet/common/constants/vehicle-categories';
import { FleetCategory } from '../../../fleet/common/enums/fleet-category';
import { SemiTrailerSize } from '../../../fleet/common/enums/semi-trailer-size';
import { FleetHelper } from '../../../fleet/common/helpers/fleet.helper';
import type {
  Fleet,
  SimplifiedFleet,
} from '../../../fleet/common/interfaces/fleet';
import type { CompanyVehicleParameter } from '../../../management-panel/dictionaries/common/interfaces/company-vehicle-parameter';
import type { ActivityModalData } from '../components/activity-modal/activity-modal.component';
import { ActivityModalComponent } from '../components/activity-modal/activity-modal.component';
import { EUROPALLET_DIMENSIONS } from '../constants/europallet-dimensions';
import { OrderPointCategory } from '../enums/order-point-category';
import { OrderSemiTrailerSize } from '../enums/order-semi-trailer-size';
import { OrderStage } from '../enums/order-stage';
import type { Order } from '../interfaces/order';
import type {
  OrderLoadingGood,
  OrderLoadingPoint,
  OrderPoint,
} from '../interfaces/order-point';
import { OrderHelper } from './order.helper';

export interface OrderSearchingVehicleData {
  iconName: OnyxIconName;
  categories: FleetCategory[];
  isLowDeck: boolean | null;
  types: string[] | null;
  parameters: Parameter[];
}

interface OrderBaseSearchingFields {
  key: string;
  name: string;
  ok: () => boolean;
  hint?: number | null;
  hintDecimalPlaces?: number;
}

interface OrderSearchingVehicleParameter extends OrderBaseSearchingFields {
  value: Parameter['value'];
  hidden?: boolean;
}

export interface OrderSearchingValidationData extends OrderBaseSearchingFields {
  showTooltip?: boolean;
}

@Service()
export class OrderModalHelper {
  private readonly modalService = inject(OnyxModalService);
  private readonly translateService = inject(TranslateService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly languagePipe = inject(OnyxLanguagePipe);

  public getSearchingVehicleParameters(
    order: Order,
    vehicle: Fleet | SimplifiedFleet | null,
    trailer: SimplifiedFleet | null,
  ): OrderSearchingVehicleParameter[] {
    const searchingData = OrderModalHelper.getSearchingVehicleData(
      order,
      this.translateService,
    );
    if (!searchingData.parameters) return [];

    const additionalParameters =
      trailer?.additionalParameters ?? vehicle?.additionalParameters;
    const activeOtherVehicleParameters = new Set(
      (additionalParameters?.otherVehicleParameters ?? []).map(
        ({ uuid }) => uuid,
      ),
    );

    const baseParameters = chain(searchingData.parameters)
      .filter(({ key }) =>
        BASE_PARAMETERS_KEYS.includes(key as keyof BaseParameters),
      )
      .filter(({ key }) => key !== 'otherVehicleParameters')
      .map((parameter) => ({
        ...parameter,
        ok: () =>
          this.evaluateParameterMatch(
            parameter.value,
            additionalParameters?.[parameter.key as keyof BaseParameters] ??
              null,
          ),
      }))
      .value();

    const companyVehicleParameters =
      (searchingData.parameters.find(
        ({ key }) => key === 'otherVehicleParameters',
      )?.value as CompanyVehicleParameter[]) ?? [];
    const otherVehicleParameters = companyVehicleParameters.map(
      ({ uuid, names }) => ({
        key: uuid,
        name: this.languagePipe.transform(names),
        value: true,
        ok: () => activeOtherVehicleParameters.has(uuid),
      }),
    );
    const cargoSpaceParameters =
      this.getCargoSpaceParameters<OrderSearchingVehicleParameter>(
        order,
        vehicle,
      );

    const specialParameters: OrderSearchingVehicleParameter[] = [
      ...(!searchingData.types?.some((type) =>
        type.includes(`${FleetCategory.VAN}-`),
      )
        ? [
            {
              key: searchingData.isLowDeck ? 'lowDeck' : 'standardDeck',
              name: searchingData.isLowDeck
                ? 'labels.lowDeck'
                : 'labels.standardDeck',
              value: true,
              ok: () =>
                !!vehicle?.additionalParameters.isLowDeck ===
                !!searchingData.isLowDeck,
            },
          ]
        : []),
      ...(!trailer && searchingData.types?.length
        ? [
            {
              key: 'vehicleType',
              name: this.joinPipe.transform(searchingData.types, {
                separator: ' / ',
                maxLength: 2,
                i18nKey: DictionaryCode.VEHICLE_TYPE,
              }),
              value: true,
              ok: () =>
                (searchingData.types ?? []).includes(
                  vehicle?.generalInformation?.type ?? '',
                ),
            },
            ...cargoSpaceParameters,
          ]
        : []),
    ];

    const customParameters = searchingData.parameters
      .filter(({ key }) =>
        ['isExpress', 'fuelTypes', 'minEmissionStandard'].includes(key),
      )
      .map((parameter) => {
        const { key, value } = parameter;
        const isOk = this.evaluateCustomMatch(key, value, vehicle);

        return {
          ...parameter,
          ok: () => isOk,
          hidden: key === 'isExpress' && !vehicle ? true : isOk,
        };
      });

    return [
      ...specialParameters,
      ...baseParameters,
      ...customParameters,
      ...otherVehicleParameters,
    ];
  }

  public getSearchingTrailerParameters(
    order: Order,
    trailer: SimplifiedFleet | null,
  ): OrderSearchingValidationData[] {
    const searchingTrailerTypes =
      OrderModalHelper.getSearchingTrailerTypes(order);
    const searchingVehicleData = OrderModalHelper.getSearchingVehicleData(
      order,
      this.translateService,
    );
    const cargoSpaceParameters = this.getCargoSpaceParameters(
      order,
      trailer,
      false,
    );

    return chain(searchingTrailerTypes ?? [])
      .thru((parameters) => [
        {
          key: 'vehicleType',
          name: this.joinPipe.transform(parameters, {
            separator: ' / ',
            maxLength: 2,
            i18nKey: DictionaryCode.VEHICLE_TYPE,
          }),
          ok: () =>
            parameters.includes(trailer?.generalInformation?.type ?? ''),
          showTooltip: parameters.length > 2,
        },
        ...(searchingVehicleData.isLowDeck
          ? [
              {
                key: 'semiTrailerSize',
                name: `${DictionaryCode.SEMI_TRAILER_SIZE}.${SemiTrailerSize.MEGA}`,
                ok: () =>
                  trailer?.additionalParameters.semiTrailerSize ===
                  SemiTrailerSize.MEGA,
              },
            ]
          : []),
        ...cargoSpaceParameters,
      ])
      .value();
  }

  public getSearchingDriversParameters(
    order: Order,
    drivers: (Driver | SimplifiedDriver)[],
  ): OrderSearchingValidationData[] {
    const {
      driverLanguages,
      hasSanitaryInspection,
      hasDoubleCrew,
      requiresDidb,
    } = order.parameters;

    const otherDriverPermissions =
      order.parameters.otherDriverPermissions?.map(({ uuid, names }) => ({
        key: uuid,
        name: this.languagePipe.transform(names),
        value: true,
        ok: () =>
          drivers.every((driver) =>
            DriverHelper.isDriverType(driver)
              ? driver.permissions.some(({ type }) => type.uuid === uuid)
              : false,
          ),
      })) ?? [];

    return [
      {
        key: 'tachograph',
        name: 'labels.tachographTime',
        ok: () => drivers.every(({ tachograph }) => !!tachograph),
      },
      ...(requiresDidb
        ? [
            {
              key: 'didb',
              name: 'labels.didb',
              ok: () =>
                drivers.every((driver) =>
                  DriverHelper.isDriverType(driver)
                    ? driver.permissions.some(
                        ({ type }) =>
                          type.icon === 'requirements-didb-outlined',
                      )
                    : false,
                ),
            },
          ]
        : []),
      ...(driverLanguages?.length
        ? [
            {
              key: 'driverLanguages',
              name: this.joinPipe.transform(driverLanguages, {
                separator: ', ',
                maxLength: 2,
                i18nKey: DictionaryCode.LANGUAGE,
              }),
              ok: () =>
                drivers.some((driver) =>
                  DriverHelper.isDriverType(driver)
                    ? driver.skills.languages?.every((language) =>
                        driverLanguages.includes(language),
                      )
                    : false,
                ),
            },
          ]
        : []),
      ...(hasDoubleCrew
        ? [
            {
              key: 'hasDoubleCrew',
              name: 'labels.hasDoubleCrew',
              ok: () => drivers.length === 2,
            },
          ]
        : []),
      ...(hasSanitaryInspection
        ? [
            {
              key: 'hasSanitaryInspection',
              name: 'labels.hasSanitaryInspection',
              ok: () =>
                drivers.every((driver) =>
                  DriverHelper.isDriverType(driver)
                    ? driver.permissions.some(
                        ({ type }) =>
                          type.icon === 'requirements-food-outlined',
                      )
                    : false,
                ),
            },
          ]
        : []),
      ...otherDriverPermissions,
    ];
  }

  public openActivityModal(data: ActivityModalData): void {
    this.modalService.open<ActivityModalData>(ActivityModalComponent, data);
  }

  private evaluateParameterMatch(
    expected: Parameter['value'],
    current: Parameter['value'] | null,
  ): boolean {
    if (current == null) return false;

    if (isString(expected) || isBoolean(expected)) {
      return isEqual(expected, current);
    }

    if (isNumber(expected)) return (current as number) >= expected;

    if (isArray(expected)) {
      return (
        intersection(current as any[], expected).length === expected.length
      );
    }

    const vehicleTemperatureRange = current as OnyxNumberRange;
    if (
      expected.from <= vehicleTemperatureRange.to &&
      vehicleTemperatureRange.from <= expected.to
    ) {
      return true;
    }

    return false;
  }

  private evaluateCustomMatch(
    key: string,
    value: Parameter['value'],
    vehicle: Fleet | SimplifiedFleet | null,
  ): boolean {
    if (!vehicle || !FleetHelper.isFleetType(vehicle)) return false;

    switch (key) {
      case 'isExpress':
        return !!value && !vehicle.tachograph;
      case 'minEmissionStandard':
        return value === vehicle.co2Emissions?.emissionStandard;
      case 'fuelTypes':
        return (value as string[]).includes(
          vehicle.fuelTankCapacity?.fuelType ?? '',
        );
      default:
        throw new Error(`Unexpected parameter key: ${key}`);
    }
  }

  private isEuropalletSize(size: OrderLoadingGood['size']): boolean {
    if (size?.type !== GoodsSizeType.DIMENSIONS) return false;

    return isEqual(
      pick(size, Object.keys(EUROPALLET_DIMENSIONS)),
      EUROPALLET_DIMENSIONS,
    );
  }

  private getLoadingPoints(points: OrderPoint[]): OrderLoadingPoint[] {
    return points.filter(
      (point) => point.category === OrderPointCategory.LOADING,
    );
  }

  private getLoadingGoods(points: OrderPoint[]): OrderLoadingGood[] {
    return this.getLoadingPoints(points).flatMap(({ goods }) => goods);
  }

  private getOrderMetrics(points: OrderPoint[]) {
    return this.getLoadingGoods(points).reduce(
      (result, good) => {
        const totalWeight = good.totalWeight;
        result.totalWeight += totalWeight;
        result.totalEuropallets += this.isEuropalletSize(good.size)
          ? (OrderHelper.getGoodQuantity(good) ?? 0)
          : 0;

        const size = good.size;
        if (!size) return result;

        if (size.type === GoodsSizeType.LOADING_METERS) {
          result.totalLoadingMeters += size.loadingMeters / 100;
        } else {
          const volume =
            size.type === GoodsSizeType.VOLUME
              ? size.volume / 1000
              : ((size.length * size.width * size.height) / 10 ** 6) *
                good.quantity!;

          result.totalVolume += volume;
        }

        return result;
      },
      {
        totalWeight: 0,
        totalVolume: 0,
        totalLoadingMeters: 0,
        totalEuropallets: 0,
      },
    );
  }

  private getCargoSpaceParameters<
    T extends OrderSearchingVehicleParameter | OrderSearchingValidationData,
  >(
    order: Order,
    fleet: Fleet | SimplifiedFleet | null,
    isVehicleParameter = true,
  ): T[] {
    const { totalVolume, totalLoadingMeters, totalEuropallets, totalWeight } =
      this.getOrderMetrics(order.points);
    const fleetVolume = (() => {
      const { length, width, height } = fleet?.cargoSpace?.dimensions ?? {};
      return (
        fleet?.cargoSpace.volumeCapacity ??
        ((length ?? 0) * (width ?? 0) * (height ?? 0)) / 10 ** 6
      );
    })();

    return [
      {
        key: 'loadCapacity',
        name: 'labels.loadCapacity',
        ...(isVehicleParameter && { value: true }),
        ok: () => (fleet?.cargoSpace?.loadCapacity ?? 0) >= totalWeight,
        hint: totalWeight,
        hintDecimalPlaces: 0,
      },
      ...(totalVolume > 0
        ? [
            {
              key: 'cubature',
              name: 'labels.cubature',
              ...(isVehicleParameter && { value: true }),
              ok: () => fleetVolume >= totalVolume,
              hint: totalVolume,
              hintDecimalPlaces: 3,
            },
          ]
        : []),
      ...(totalLoadingMeters > 0
        ? [
            {
              key: 'loadingMeters',
              name: 'labels.loadingMeters',
              ...(isVehicleParameter && { value: true }),
              ok: () =>
                (fleet?.cargoSpace.dimensions.length ?? 0) >=
                totalLoadingMeters,
              hint: totalLoadingMeters,
              hintDecimalPlaces: 2,
            },
          ]
        : []),
      ...(totalEuropallets > 0
        ? [
            {
              key: 'europalletsNumber',
              name: 'labels.europalletsNumber',
              ...(isVehicleParameter && { value: true }),
              ok: () =>
                (fleet?.cargoSpace?.europalletsNumber ?? 0) >= totalEuropallets,
              hint: totalEuropallets,
              hintDecimalPlaces: 0,
            },
          ]
        : []),
    ] as T[];
  }

  public static getSearchingVehicleData(
    order: Order,
    translateService: TranslateService,
  ): OrderSearchingVehicleData {
    const { vehicleTypes, minSemiTrailerSize } = order.basicInformation;

    const areAllVans = vehicleTypes.every((type) =>
      type.startsWith(`${FleetCategory.VAN}-`),
    );
    const parameters = CommonHelper.getSortedParameters(
      order.parameters,
      translateService,
    );

    if (areAllVans) {
      return {
        iconName: 'van-filled' as const,
        categories: [FleetCategory.VAN],
        isLowDeck: null,
        types: vehicleTypes,
        parameters,
      };
    }

    const { data, registrationNumber } =
      order.basicInformation.loadedSemiTrailer ?? {};
    if (
      data ||
      registrationNumber ||
      minSemiTrailerSize !== OrderSemiTrailerSize.AUTO
    ) {
      const isLowDeck =
        data?.additionalParameters?.semiTrailerSize === SemiTrailerSize.MEGA ||
        minSemiTrailerSize === OrderSemiTrailerSize.MEGA;

      return {
        iconName: 'semi-truck-filled' as const,
        categories: [FleetCategory.SEMI_TRUCK],
        isLowDeck,
        types: null,
        parameters,
      };
    }

    return {
      iconName: 'vehicle-filled' as const,
      categories: [...VEHICLE_CATEGORIES],
      isLowDeck: null,
      types: vehicleTypes,
      parameters,
    };
  }

  public static getSearchingTrailerTypes(order: Order): string[] | null {
    const { vehicleTypes, minSemiTrailerSize } = order.basicInformation;
    const { uuid, registrationNumber } =
      order.basicInformation.loadedSemiTrailer ?? {};
    if (
      uuid ||
      registrationNumber ||
      minSemiTrailerSize !== OrderSemiTrailerSize.AUTO
    ) {
      return vehicleTypes;
    }

    return vehicleTypes;
  }

  public static getOrderStageIcon(stage: OrderStage): OnyxIconName {
    return (
      {
        [OrderStage.CREATION]: 'good-filled',
        [OrderStage.ROUTE_VERIFICATION]: 'route-filled',
        [OrderStage.VEHICLE_SEARCH]: 'ai-filled',
        [OrderStage.ENGINE_PROPOSAL]: 'decision-filled',
        [OrderStage.MANUAL_PROPOSAL]: 'manual-filled',
        [OrderStage.OUTSOURCING]: 'cursor-filled',
        [OrderStage.REALIZATION]: 'vehicle-ride-filled',
        [OrderStage.COMPLETING_DOCUMENTS]: 'document-filled',
        [OrderStage.WAITING_FOR_COLLECTIVE_INVOICE]: 'vehicle-waiting-filled',
        [OrderStage.INVOICE_TO_ISSUE]: 'document-filled',
        [OrderStage.PAYMENT_CONFIRMATION]: 'finance-filled',
      } as const
    )[stage];
  }
}
